import { useState, useEffect } from "react";
import { useGetInvolvedModal } from "@/contexts/GetInvolvedModalContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  NotePencil,
  TreeStructure,
  Leaf,
  FileText,
  CloudRain,
  Drop,
  Scales,
  Newspaper,
  FilePdf,
  ArrowSquareOut,
  Users,
  CalendarBlank,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RESEARCH_IMG = "https://customer-assets.emergentagent.com/job_summ-ai-1/artifacts/zxzv6upd_4096646951_d3f801355e_b.jpg";

const resourceIcons = [NotePencil, TreeStructure, Leaf, FileText, CloudRain, Drop, Scales, Newspaper];

export default function ResearchPage() {
  const { openModal } = useGetInvolvedModal();
  const { t } = useLanguage();
  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");
  const resourceTypes = t("resource_types") || [];

  const filteredReports = activeFilter === "All"
    ? reports
    : reports.filter((r) => r.category === activeFilter);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/reports`)
      .then((r) => r.json())
      .then((data) => setReports(Array.isArray(data) ? data : []))
      .catch(() => setReports([]))
      .finally(() => setLoadingReports(false));

    fetch(`${BACKEND_URL}/api/report-categories`)
      .then((r) => r.json())
      .then((data) => setCategories(Array.isArray(data) ? data.map((c) => c.name) : []))
      .catch(() => setCategories([]));
  }, []);

  return (
    <div data-testid="research-page">
      {/* Page Header */}
      <section
        className="py-20 md:py-28 border-b"
        style={{ backgroundColor: '#E8EDE9', borderColor: '#D3DCD6' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p
            className="text-xs uppercase tracking-[0.2em] font-medium mb-4"
            style={{ color: '#B85C38' }}
          >
            {t("research_tagline")}
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium"
            style={{ color: '#1C211F' }}
          >
            {t("research_heading")}
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
            <div className="md:col-span-5">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={RESEARCH_IMG}
                  alt="Research and documentation"
                  className="w-full h-64 md:h-80 object-cover"
                  data-testid="research-image"
                />
              </div>
            </div>
            <div className="md:col-span-7 flex flex-col justify-center">
              <p
                className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
                style={{ color: '#B85C38' }}
              >
                {t("knowledge_tagline")}
              </p>
              <h2
                className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-5"
                style={{ color: '#1C211F' }}
              >
                {t("knowledge_heading")}
              </h2>
              <p className="text-base leading-relaxed" style={{ color: '#4A5450' }}>
                {t("knowledge_desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Publications */}
      <section
        data-testid="recent-publications-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#E8EDE9' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p
              className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
              style={{ color: '#B85C38' }}
            >
              {t("publications_tagline")}
            </p>
            <h2
              className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium"
              style={{ color: '#1C211F' }}
            >
              {t("publications_heading")}
            </h2>
          </div>

          {/* Category Filters */}
          {!loadingReports && reports.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2.5 mb-10" data-testid="report-filters">
              {["All", ...categories].map((cat) => {
                const isActive = activeFilter === cat;
                const count = cat === "All" ? reports.length : reports.filter((r) => r.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveFilter(cat)}
                    data-testid={`filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="px-4 py-2 rounded-full text-xs font-medium border transition-all duration-200"
                    style={{
                      backgroundColor: isActive ? '#1A5C37' : '#F7F5F0',
                      color: isActive ? '#F7F5F0' : '#1A5C37',
                      borderColor: isActive ? '#1A5C37' : 'rgba(26,92,55,0.25)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(26,92,55,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = '#F7F5F0';
                    }}
                  >
                    {cat} <span className="opacity-70 ml-1">({count})</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Loading state */}
          {loadingReports ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: '#1A5C37', borderTopColor: 'transparent' }} />
            </div>
          ) : reports.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(26,92,55,0.08)' }}>
                <FilePdf size={28} weight="duotone" style={{ color: '#1A5C37' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: '#1C211F' }}>{t("no_publications")}</p>
              <p className="text-xs mt-1" style={{ color: '#4A5450' }}>{t("no_publications_sub")}</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(26,92,55,0.08)' }}>
                <FilePdf size={28} weight="duotone" style={{ color: '#1A5C37' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: '#1C211F' }}>No reports in this category</p>
              <p className="text-xs mt-1" style={{ color: '#4A5450' }}>Try a different category or view all.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredReports.map((r, i) => (
                <a
                  key={r.id}
                  href={`${BACKEND_URL}${r.file_url}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`publication-card-${i}`}
                  className="text-left p-6 rounded-lg border transition-all duration-200 group flex flex-col no-underline"
                  style={{
                    backgroundColor: '#F7F5F0',
                    borderColor: 'rgba(26, 92, 55, 0.1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(26, 92, 55, 0.35)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(26,92,55,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(26, 92, 55, 0.1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(26, 92, 55, 0.08)' }}
                  >
                    <FilePdf size={20} weight="duotone" style={{ color: '#1A5C37' }} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span
                      className="text-xs font-medium px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: 'rgba(184, 92, 56, 0.1)', color: '#B85C38' }}
                    >
                      {r.category}
                    </span>
                    <span
                      className="flex items-center gap-1 text-xs"
                      style={{ color: '#4A5450' }}
                    >
                      <CalendarBlank size={12} weight="duotone" />
                      {r.year}
                    </span>
                  </div>
                  <h3
                    className="font-heading text-lg font-medium mb-2 leading-snug group-hover:underline"
                    style={{ color: '#1C211F' }}
                  >
                    {r.title}
                  </h3>
                  <p
                    className="flex items-start gap-1.5 text-xs mb-3"
                    style={{ color: '#4A5450' }}
                  >
                    <Users size={12} weight="duotone" className="mt-0.5 flex-shrink-0" />
                    {r.publisher}
                  </p>
                  <p className="text-sm leading-relaxed flex-1" style={{ color: '#4A5450' }}>
                    {r.description}
                  </p>
                  <div className="flex items-center gap-1.5 mt-4 text-xs font-medium" style={{ color: '#1A5C37' }}>
                    <ArrowSquareOut size={14} weight="duotone" />
                    {t("open_pdf")}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Resource Types Grid */}
      <section
        data-testid="resources-grid-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p
              className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
              style={{ color: '#B85C38' }}
            >
              {t("what_we_publish_tagline")}
            </p>
            <h2
              className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium"
              style={{ color: '#1C211F' }}
            >
              {t("resource_categories_heading")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {resourceTypes.map((resource, i) => {
              const Icon = resourceIcons[i] || FileText;
              return (
                <div
                  key={i}
                  data-testid={`resource-card-${i}`}
                  className="kybia-card p-6 rounded-lg border"
                  style={{
                    backgroundColor: '#F7F5F0',
                    borderColor: 'rgba(26, 92, 55, 0.1)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(26, 92, 55, 0.08)' }}
                  >
                    <Icon size={20} weight="duotone" style={{ color: '#1A5C37' }} />
                  </div>
                  <h3
                    className="font-heading text-lg font-medium mb-2"
                    style={{ color: '#1C211F' }}
                  >
                    {resource.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#4A5450' }}>
                    {resource.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#E8EDE9' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-5"
            style={{ color: '#1C211F' }}
          >
            {t("research_cta_heading")}
          </h2>
          <p className="text-base leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: '#4A5450' }}>
            {t("research_cta_desc")}
          </p>
          <button
            onClick={openModal}
            data-testid="research-submit-btn"
            className="inline-block px-8 py-3.5 rounded-full text-sm font-medium transition-colors duration-200"
            style={{ backgroundColor: '#B85C38', color: '#F7F5F0' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#964B2E'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#B85C38'}
          >
            {t("submit_research")}
          </button>
        </div>
      </section>

    </div>
  );
}
